import React, { useState } from 'react';
import { Users, TrendingUp, BarChart2, Plus, Edit2, Trash2, Save } from 'lucide-react';
import toast from 'react-hot-toast';

interface Competitor {
  id: string;
  name: string;
  platform: string;
  followers: string;
  engagement: string;
  postFrequency: string;
  topContent: string;
  growth: string;
  notes: string;
}

export default function CompetitorAnalytics() {
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<Omit<Competitor, 'id'>>({
    name: '',
    platform: 'instagram',
    followers: '',
    engagement: '',
    postFrequency: '',
    topContent: '',
    growth: '',
    notes: ''
  });

  const handleAdd = () => {
    const newCompetitor = {
      id: crypto.randomUUID(),
      ...formData
    };
    setCompetitors([...competitors, newCompetitor]);
    setShowAddForm(false);
    setFormData({
      name: '',
      platform: 'instagram',
      followers: '',
      engagement: '',
      postFrequency: '',
      topContent: '',
      growth: '',
      notes: ''
    });
    toast.success('Competidor adicionado com sucesso!');
  };

  const handleEdit = (competitor: Competitor) => {
    setIsEditing(competitor.id);
    setFormData(competitor);
  };

  const handleSave = (id: string) => {
    setCompetitors(competitors.map(comp => 
      comp.id === id ? { ...formData, id } : comp
    ));
    setIsEditing(null);
    toast.success('Alterações salvas com sucesso!');
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este competidor?')) {
      setCompetitors(competitors.filter(comp => comp.id !== id));
      toast.success('Competidor removido com sucesso!');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Análise de Competidores
        </h3>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Adicionar Competidor
        </button>
      </div>

      {showAddForm && (
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <h4 className="font-medium text-gray-900 mb-4">Novo Competidor</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome/Usuário
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="@usuario"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plataforma
              </label>
              <select
                value={formData.platform}
                onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="instagram">Instagram</option>
                <option value="youtube">YouTube</option>
                <option value="tiktok">TikTok</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seguidores
              </label>
              <input
                type="text"
                value={formData.followers}
                onChange={(e) => setFormData({ ...formData, followers: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="100K"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Taxa de Engajamento
              </label>
              <input
                type="text"
                value={formData.engagement}
                onChange={(e) => setFormData({ ...formData, engagement: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="4.2%"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Frequência de Posts
              </label>
              <input
                type="text"
                value={formData.postFrequency}
                onChange={(e) => setFormData({ ...formData, postFrequency: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="5/semana"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Crescimento
              </label>
              <input
                type="text"
                value={formData.growth}
                onChange={(e) => setFormData({ ...formData, growth: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="+12%"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Conteúdo Principal
            </label>
            <input
              type="text"
              value={formData.topContent}
              onChange={(e) => setFormData({ ...formData, topContent: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="Tutoriais, vlogs, etc."
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observações
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              rows={3}
              placeholder="Notas sobre estratégias, pontos fortes, etc."
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancelar
            </button>
            <button
              onClick={handleAdd}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Adicionar
            </button>
          </div>
        </div>
      )}

      {competitors.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">
            Nenhum competidor adicionado
          </h4>
          <p className="text-gray-600 mb-4">
            Adicione competidores para acompanhar seu desempenho
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {competitors.map((competitor) => (
            <div key={competitor.id} className="bg-gray-50 rounded-lg p-6">
              {isEditing === competitor.id ? (
                <div className="space-y-4">
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600">Seguidores</label>
                      <input
                        type="text"
                        value={formData.followers}
                        onChange={(e) => setFormData({ ...formData, followers: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600">Engajamento</label>
                      <input
                        type="text"
                        value={formData.engagement}
                        onChange={(e) => setFormData({ ...formData, engagement: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => handleSave(competitor.id)}
                    className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    <Save className="h-4 w-4 inline mr-2" />
                    Salvar
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-gray-900">{competitor.name}</h4>
                      <span className="text-sm text-gray-500">({competitor.platform})</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(competitor)}
                        className="p-1 text-gray-600 hover:text-gray-900"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(competitor.id)}
                        className="p-1 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Seguidores</span>
                      <span className="font-medium">{competitor.followers}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Engajamento</span>
                      <span className="font-medium">{competitor.engagement}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Posts/Semana</span>
                      <span className="font-medium">{competitor.postFrequency}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Crescimento</span>
                      <span className="font-medium text-green-600">{competitor.growth}</span>
                    </div>
                    <div className="pt-3 border-t border-gray-200">
                      <span className="text-gray-600 block mb-1">Conteúdo Principal</span>
                      <span className="font-medium text-indigo-600">{competitor.topContent}</span>
                    </div>
                    {competitor.notes && (
                      <div className="pt-3 border-t border-gray-200">
                        <span className="text-gray-600 block mb-1">Observações</span>
                        <p className="text-sm text-gray-700">{competitor.notes}</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}